import { useAppForm } from '@sparklayer-io/spark-dash-core/lib/app-form';
import { t } from 'i18next';
import { useState } from 'react';
import type { I18nPrefix } from '../../../i18n/prefix';
import type { DashboardHandlers } from '../../../lib/dashboard-page';
import { isAuthValidationError } from '../schemas';
import type { Invitation, RegisterFormValues, RegisterResponse, RegisterType } from '../types';
import { AuthAlert } from './auth-alert';
import { AuthButton } from './auth-button';
import { CompanyWebsiteInput } from './company-website-input';
import { FloatingInput } from './floating-input';
import { FloatingSelect } from './floating-select';
import { TermsCheckbox } from './terms-checkbox';

const COMPONENT = 'features:auth.components.register_form' satisfies I18nPrefix;
const COMMON = 'features:auth.components.common' satisfies I18nPrefix;

type RegisterFormProps = {
  handlers: DashboardHandlers;
  registerType?: RegisterType;
  invitation?: Invitation;
  email?: string | null;
};

export function RegisterForm({
  handlers,
  registerType = 'default',
  invitation,
  email,
}: RegisterFormProps) {
  const { apiHandler, authHandler, navigationHandler } = handlers;
  const [loading, setLoading] = useState<boolean>(false);
  const [bannerMessages, setBannerMessages] = useState<string[]>([]);

  const query = navigationHandler.query();
  const roleQuery = query.role;
  const planKey = query.plan;

  const form = useAppForm({
    defaultValues: {
      email: email ?? query.email ?? '',
      name: query.name ?? '',
      companyName: query.companyName ?? '',
      companyWebsite: query.companyWebsite ?? '',
      password: '',
      role: roleQuery && ['merchant', 'partner'].includes(roleQuery) ? roleQuery : '',
    } satisfies RegisterFormValues,
    onSubmit: async ({ value }) => {
      setBannerMessages([]);
      setLoading(true);
      try {
        await apiHandler.fetch({ method: 'GET', path: '/sanctum/csrf-cookie', maxRedirects: 0 });

        const res = await apiHandler.fetch<RegisterResponse>({
          method: 'POST',
          path: '/register',
          payload: {
            email: value.email,
            name: value.name,
            company_name: registerType === 'team-member' ? undefined : value.companyName,
            company_website:
              registerType === 'team-member' ? undefined : `https://${value.companyWebsite}`,
            app_registration_source: query.app_registration_source ?? 'dashboard_registration',
            password: value.password,
            is_partner: registerType === 'partner',
            role: registerType === 'partner' ? 'partner' : value.role,
            plan: planKey ? { key: planKey, trial_end: query.trial_end ?? null } : null,
          },
        });

        await authHandler.reloadUser();

        if (invitation) {
          const redirectUrl = new URL(
            `https://${window.location.hostname}/dashboard-api/team-invitations/${encodeURIComponent(invitation.invitationId)}`,
          );
          redirectUrl.searchParams.append('signature', invitation.signature);
          window.location.href = redirectUrl.toString();
        } else if (res.redirect_url) {
          window.location.href = res.redirect_url;
          return;
        } else {
          const isPartnerRegistration = registerType === 'partner' || value.role === 'partner';
          navigationHandler.navigate(
            isPartnerRegistration ? '/partners' : '/integrations/platform',
            { excludeSiteId: true, excludeSiteEnv: true },
          );
        }
      } catch (err) {
        setLoading(false);
        if (isAuthValidationError(err)) {
          const messages = [...(err.data.message ? [err.data.message] : [])];
          for (const fieldMessages of Object.values(err.data.errors)) {
            messages.push(...fieldMessages);
          }
          setBannerMessages(Array.from(new Set(messages)));
        } else {
          setBannerMessages([t(`${COMMON}.generic_error`)]);
          throw err;
        }
        return;
      }
      setLoading(false);
    },
  });

  const roleOptions =
    registerType === 'team-member'
      ? [
          { value: '', text: t(`${COMPONENT}.role.placeholder`), disabled: true },
          { value: 'merchant', text: t(`${COMPONENT}.role.merchant_team_member`) },
          { value: 'partner', text: t(`${COMPONENT}.role.partner_team_member`) },
        ]
      : [
          { value: '', text: t(`${COMPONENT}.role.placeholder`), disabled: true },
          { value: 'merchant', text: t(`${COMPONENT}.role.merchant_default`) },
          { value: 'partner', text: t(`${COMPONENT}.role.partner_default`) },
        ];

  return (
    <form
      className="space-y-3"
      onSubmit={(event) => {
        event.preventDefault();
        void form.handleSubmit();
      }}
    >
      {bannerMessages.length > 0 && (
        <AuthAlert type="warning">
          {bannerMessages.map((message) => (
            <p key={message} className="text-sm">
              {message}
            </p>
          ))}
        </AuthAlert>
      )}

      {registerType !== 'team-member' && (
        <form.Field name="email">
          {(field) => (
            <FloatingInput
              name="email"
              type="email"
              label={t(`${COMPONENT}.email_label`)}
              autoComplete="email"
              required
              value={field.state.value}
              onChange={field.handleChange}
            />
          )}
        </form.Field>
      )}

      <form.Field name="name">
        {(field) => (
          <FloatingInput
            name="name"
            label={t(`${COMPONENT}.name_label`)}
            autoComplete="name"
            required
            value={field.state.value}
            onChange={field.handleChange}
          />
        )}
      </form.Field>

      {registerType !== 'team-member' && (
        <>
          <form.Field name="companyName">
            {(field) => (
              <FloatingInput
                name="company-name"
                label={t(`${COMPONENT}.company_name_label`)}
                required
                value={field.state.value}
                onChange={field.handleChange}
              />
            )}
          </form.Field>

          <form.Field name="companyWebsite">
            {(field) => (
              <CompanyWebsiteInput
                required
                value={field.state.value}
                onChange={field.handleChange}
              />
            )}
          </form.Field>
        </>
      )}

      <form.Field name="password">
        {(field) => (
          <FloatingInput
            name="password"
            type="password"
            label={t(`${COMPONENT}.password_label`)}
            minLength={12}
            maxLength={128}
            required
            value={field.state.value}
            onChange={field.handleChange}
          />
        )}
      </form.Field>

      {registerType !== 'partner' && (
        <form.Field name="role">
          {(field) => (
            <FloatingSelect
              name="role"
              label={t(
                registerType === 'team-member'
                  ? `${COMPONENT}.role.team_member_label`
                  : `${COMPONENT}.role.default_label`,
              )}
              required
              options={roleOptions}
              value={field.state.value}
              onChange={field.handleChange}
            />
          )}
        </form.Field>
      )}

      <TermsCheckbox
        agreePrefix={t(`${COMMON}.terms.agree_prefix`)}
        linkText={
          registerType === 'partner'
            ? t(`${COMPONENT}.partner_terms_of_service`)
            : t(`${COMMON}.terms.terms_of_service`)
        }
        linkHref={
          registerType === 'partner'
            ? 'https://www.sparklayer.io/terms-of-service-partners/'
            : 'https://www.sparklayer.io/terms-of-service'
        }
      />

      <div>
        <AuthButton loading={loading}>{t(`${COMPONENT}.submit`)}</AuthButton>
      </div>
    </form>
  );
}
